import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Callout, Field, Form, Section } from '@beyond-js/ui/react';
import { Proposal, type AllowanceChange, type AllowanceReading, type Allowances, type EndTerms, type Field as Name, type GrantTerms } from '@snapshots/domain';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Failure, Outage, Problems } from '@snapshots/ui';

/** A change as reviewed or as it is on its way: its operation and the exact terms it sends */
type Step = { operation: 'grant' | 'end'; terms: GrantTerms | EndTerms };

/** What the last change answered: what it made, or the refusal that decided it or left it unknown */
type Answer = { made: AllowanceChange; operation: 'grant' | 'end' } | { refusal: Refusal; operation: 'grant' | 'end' };

/**
 * Granting, replacing and ending an organization's allowance, each decided in two steps: the operator writes the
 * terms, reviews what they reach (who can do what, until when, what it replaces, that nothing is charged, that the
 * packages stay readable and how to undo it) and only then sends them. The terms are the proposal's (`Proposal`):
 * they stay through every refusal, an unknown outcome and a sign-in again.
 *
 * One change at a time: from the press that sends it until the page is read again after its answer, nothing else
 * is sent and nothing is written, the review's Edit terms and the other form wait, and a second press does
 * nothing. A change whose answer did not arrive is the proposal's `recovery`, exactly as it was sent: it is shown
 * with its own terms and the allowance it was decided on, sent again under its key (which answers what the first
 * send made, even after a reload, a sign-in again or another allowance coming into force) or forgotten, and no other
 * change starts until one of those. A change refused because the allowance changed meanwhile is reviewed again.
 */
export function Changes({ reading, proposal, allowances, reread }: { reading: AllowanceReading; proposal: Proposal; allowances: Allowances; reread: () => Promise<unknown> }) {
	const { text, language, date } = usePresence();
	const [, setTick] = useState(0);
	const [review, setReview] = useState<Step>();
	const [busy, setBusy] = useState(false);
	const [answer, setAnswer] = useState<Answer>();
	// Taken before anything is drawn, so two presses in one frame never send two changes
	const owner = useRef(false);
	const form = useRef<HTMLFormElement>(null);
	const organization = reading.organization.id;
	const current = reading.current;
	const bounds = reading.bounds;
	const recovery = proposal.recovery;
	const update = (field: Name, value: string) => {
		if (owner.current) return;
		proposal.set(field, value);
		setTick(value => value + 1);
	};
	// A review is of the allowance it was made on: another one in force closes it, the terms kept
	useEffect(() => setReview(undefined), [current?.id]);

	const send = async (step: Step) => {
		if (owner.current) return;
		owner.current = true;
		setBusy(true);
		setAnswer(undefined);
		try {
			const key = proposal.key(step.operation, step.terms);
			const made = step.operation === 'grant' ? await allowances.grant(organization, step.terms as GrantTerms, key) : await allowances.end(organization, step.terms as EndTerms, key);
			proposal.settle(step.operation);
			setReview(undefined);
			setAnswer({ made, operation: step.operation });
			await reread();
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			if (refusal.quiet) return;
			// An outage or no answer leaves the change undecided, and a sign-in again sends the same one: it stays on its way
			const undecided = refusal.uncertain || Boolean(Outage.cause(refusal)) || refusal.code === 'REAUTHENTICATION_REQUIRED';
			if (!undecided) proposal.settle();
			setReview(undefined);
			setAnswer({ refusal, operation: step.operation });
			if (refusal.code === 'STATE_CONFLICT') await reread();
		} finally {
			owner.current = false;
			setBusy(false);
			setTick(value => value + 1);
		}
	};
	const forget = () => {
		if (owner.current) return;
		proposal.forget();
		setAnswer(undefined);
		setTick(value => value + 1);
	};

	const { missing, wrong } = proposal.check(bounds);
	const draft = proposal.draft;
	const mib = Math.floor(bounds.bytes / Proposal.UNIT);
	const error = (field: Name, words: string) => (wrong.includes(field) ? words : null);
	const grant = proposal.grant(current?.id ?? null, bounds);
	const ending = current ? proposal.end(current.id) : undefined;
	const place = () => form.current?.querySelector<HTMLElement>(missing.length ? `[name="${missing[0]}"]` : '[aria-invalid="true"]') ?? null;
	const blocked = reading.organization.state !== 'active';
	// Nothing new while a change runs or one is still on its way
	const held = busy || Boolean(recovery);

	const said = answer && <Said answer={answer} minutes={Math.round(reading.recent / 60)} />;
	const reach = (terms: GrantTerms) => {
		const replaced = terms.expected ? reading.allowances.find(entry => entry.id === terms.expected) : undefined;
		return [
			text('operator.review.reach', { organization, size: Proposal.size(terms.limits.bytes, language), sources: terms.limits.sources, until: terms.ends ? date(terms.ends) : text('operator.review.until.none') }),
			replaced ? text('operator.review.replaces', { date: date(replaced.granted) }) : null,
			text('operator.review.charges'),
			text('operator.review.undo')
		];
	};
	const ends = [text('operator.review.end.reach', { organization }), text('operator.review.end.kept'), text('operator.review.end.again')];
	// A change being sent shows as it was reviewed; the recovery is for one left undecided
	const pending = recovery && !(busy && review) && (
		<Recover lines={recovery.operation === 'grant' ? reach(recovery.terms as GrantTerms) : ends} busy={busy} onSend={() => void send(recovery)} onCheck={() => void reread()} onForget={forget} />
	);

	return (
		<>
			<Section title={text(current ? 'operator.section.replace' : 'operator.section.grant')}>
				{pending && recovery?.operation === 'grant' ? (
					pending
				) : review?.operation === 'grant' ? (
					<Review lines={reach(review.terms as GrantTerms)} confirm={text(current ? 'operator.confirm.replace' : 'operator.confirm.grant')} busy={busy} onConfirm={() => void send(review)} onEdit={() => !owner.current && setReview(undefined)} />
				) : (
					<Form ref={form} className="flex flex-col gap-4" data-allowance-form="grant" onSubmit={event => (event.preventDefault(), grant && !blocked && !held && !owner.current && setReview({ operation: 'grant', terms: grant }))}>
						<Field label={text('operator.field.plan')} hint={text('operator.field.plan.hint')} error={error('plan', text('operator.field.plan.wrong'))}>
							<input name="plan" required readOnly={held} autoComplete="off" spellCheck={false} value={draft.plan} aria-invalid={wrong.includes('plan') || undefined} onChange={event => update('plan', event.target.value)} />
						</Field>
						<Field label={text('operator.field.bytes')} hint={text('operator.field.bytes.hint', { max: mib })} error={error('bytes', text('operator.field.bytes.wrong', { max: mib }))}>
							<input name="bytes" required readOnly={held} inputMode="numeric" value={draft.bytes} aria-invalid={wrong.includes('bytes') || undefined} onChange={event => update('bytes', event.target.value)} />
						</Field>
						<Field label={text('operator.field.sources')} hint={text('operator.field.sources.hint', { max: bounds.sources })} error={error('sources', text('operator.field.sources.wrong', { max: bounds.sources }))}>
							<input name="sources" required readOnly={held} inputMode="numeric" value={draft.sources} aria-invalid={wrong.includes('sources') || undefined} onChange={event => update('sources', event.target.value)} />
						</Field>
						<Field label={text('operator.field.ends')} optional hint={text('operator.field.ends.hint', { days: bounds.days })} error={error('ends', text('operator.field.ends.wrong', { days: bounds.days }))}>
							<input name="ends" type="date" readOnly={held} value={draft.ends} aria-invalid={wrong.includes('ends') || undefined} onChange={event => update('ends', event.target.value)} />
						</Field>
						<Field label={text('operator.field.reason')} hint={text('operator.field.reason.hint')} error={error('reason', text('operator.field.reason.wrong'))}>
							<textarea name="reason" required readOnly={held} rows={2} value={draft.reason} aria-invalid={wrong.includes('reason') || undefined} onChange={event => update('reason', event.target.value)} />
						</Field>
						<div>
							<Button type="submit" variant="primary" label={text('operator.review.grant')} waiting={!grant || blocked || held} place={place} />
						</div>
					</Form>
				)}
				{answer?.operation === 'grant' && said}
			</Section>
			{(current || recovery?.operation === 'end') && (
				<Section title={text('operator.section.end')}>
					{pending && recovery?.operation === 'end' ? (
						pending
					) : review?.operation === 'end' ? (
						<Review lines={ends} confirm={text('operator.confirm.end')} danger busy={busy} onConfirm={() => void send(review)} onEdit={() => !owner.current && setReview(undefined)} />
					) : (
						<Form className="flex flex-col gap-4" data-allowance-form="end" onSubmit={event => (event.preventDefault(), ending && !held && !owner.current && setReview({ operation: 'end', terms: ending }))}>
							<Field label={text('operator.field.ending')} hint={text('operator.field.ending.hint')}>
								<textarea name="ending" required readOnly={held} rows={2} value={draft.ending} onChange={event => update('ending', event.target.value)} />
							</Field>
							<div>
								<Button type="submit" variant="secondary" label={text('operator.review.end')} waiting={!ending || held} />
							</div>
						</Form>
					)}
					{answer?.operation === 'end' && said}
				</Section>
			)}
		</>
	);
}

/** A change on its way, with its own terms: sent again as it was, checked, or forgotten */
function Recover({ lines, busy, onSend, onCheck, onForget }: { lines: Array<string | null>; busy: boolean; onSend: () => void; onCheck: () => void; onForget: () => void }) {
	const { text } = usePresence();
	return (
		<div className="flex flex-col gap-3" data-allowance-review data-allowance-recovery>
			<p className="font-semibold">{text('operator.recover.title')}</p>
			{lines.filter(Boolean).map(line => (
				<p key={line}>{line}</p>
			))}
			<p>{text('operator.unknown.text')}</p>
			<div className="flex flex-wrap gap-3">
				<Button variant="primary" label={text('operator.unknown.again')} busy={busy} onClick={onSend} />
				<Button variant="secondary" label={text('operator.unknown.check')} waiting={busy} onClick={onCheck} />
				<Button variant="quiet" label={text('operator.recover.forget')} waiting={busy} onClick={onForget} />
			</div>
		</div>
	);
}

/** What a change will reach, said once before it is sent, with the change's own verb */
function Review({ lines, confirm, danger, busy, onConfirm, onEdit }: { lines: Array<string | null>; confirm: string; danger?: boolean; busy: boolean; onConfirm: () => void; onEdit: () => void }) {
	const { text } = usePresence();
	return (
		<div className="flex flex-col gap-3" data-allowance-review>
			<p className="font-semibold">{text('operator.review.title')}</p>
			{lines.filter(Boolean).map(line => (
				<p key={line}>{line}</p>
			))}
			<div className="flex flex-wrap gap-3">
				<Button variant={danger ? 'danger' : 'primary'} label={confirm} busy={busy} onClick={onConfirm} />
				<Button variant="quiet" label={text('operator.edit')} waiting={busy} onClick={onEdit} />
			</div>
		</div>
	);
}

/** One answer of a change, in place: made, refused with its cause and the way on, or not known */
function Said({ answer, minutes }: { answer: Answer; minutes: number }) {
	const { text, language } = usePresence();
	if ('made' in answer) {
		const key = `operator.made.${answer.operation}${answer.made.replayed ? '.replayed' : ''}`;
		return <Callout tone="success" live title={text(key)} />;
	}
	const { refusal } = answer;
	const callout = (title: string, body: string, actions?: ReactNode) => (
		<div data-failure={refusal.code} data-request={refusal.request}>
			<Callout tone="warning" live title={title} body={body} actions={actions} details={{ ...Problems.details(refusal), locale: language }} />
		</div>
	);
	// The change stays on its way: what it is and its ways on are the recovery's, above
	if (refusal.uncertain || Outage.cause(refusal)) return <Failure refusal={refusal} text={text} locale={language} />;
	if (refusal.code === 'REAUTHENTICATION_REQUIRED') return callout(text('operator.reauth.title'), text('operator.reauth.text', { minutes }));
	if (refusal.code === 'STATE_CONFLICT' && refusal.details?.reason === 'organization') return callout(text('operator.unavailable.title'), text(`operator.state.${String(refusal.details.state)}`));
	if (refusal.code === 'STATE_CONFLICT') return callout(text('operator.expected.title'), text('operator.expected.text'));
	if (refusal.code === 'CONFLICT') return callout(text('operator.key.title'), text('operator.key.text'));
	if (refusal.code === 'FORBIDDEN') return callout(text('operator.unavailable.title'), text('operator.forbidden'));
	return <Failure refusal={refusal} text={text} locale={language} />;
}
